import { useEffect, useState } from "react";
import {
  BsExclamationTriangleFill,
  BsQuestionCircle,
} from "react-icons/bs/index.js";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile } from "../redux/profile/actions";
import { LazyLoadImage } from "react-lazy-load-image-component";

// FUNCTIONS
import { suggestFeatures } from "../utility/helper";

// STYLES
import styles from "../styles/StartWizard.module.css";

// IMAGES
import logo from "../asset/Images/cryptocrispyLogo.png";

import ExchangesModal from "./ExchangesModal";
import exchange from "../asset/Images/exchange.jpeg";
import risk1 from "../asset/Images/risk1.jpg";
import TopExchanges from "./start-wizard/TopExchanges";
import { getTrustedIps } from "../redux/exchange/actions";
import { AiTwotoneFire } from "react-icons/ai";
import { HiRocketLaunch } from "react-icons/hi2";
import { FaBalanceScale, FaShieldAlt } from "react-icons/fa";
import ErrorComponent from "../components/errors/ErrorComponent";
import usePageTitle from "../hooks/usePageTitle";
import ExchangeConnectionSettingsMenu from "./connect-to-exchange/ExchangeConnectionSettingsMenu";

const StartWizard = function () {
  const [step, setStep] = useState(0);
  const [riskAppetite, setRiskAppetite] = useState("3");
  const [showExchanges, setShowExchanges] = useState(false);
  const [answers] = useState({
    1: "",
    2: "",
    3: "",
    4: "",
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  let {
    main: { userData },
    exchangeState: { trustedIps, getTrustedIpsStatus, exchangesError },
  } = useSelector((state) => state);

  usePageTitle("Suggestion Features");

  const handleClick = (e) => {
    e.preventDefault();
    dispatch(
      updateProfile(
        userData?.email,
        userData["custom:custom_username"],
        ["suggested_features", "answer_date", "ai_assistant_risk_appetite"],
        [
          suggestFeatures(answers[1], answers[2], answers[3], answers[4]).join(
            ", ",
          ),
          new Date(),
          riskAppetite,
        ],
        false,
      ),
    );
    // navigate("/ai-assistant/assistant");
    navigate("/dashboard");
  };

  const changeRiskHandler = (e) => {
    if (e.target.closest("span")) {
      setRiskAppetite(e.target.id);
    }
  };

  const goToSecondStep = () => {
    setStep(1);
  };

  useEffect(() => {
    if (
      userData &&
      Object.keys(userData)?.length &&
      !trustedIps.length &&
      getTrustedIpsStatus !== "error" &&
      getTrustedIpsStatus !== "loading"
    ) {
      dispatch(getTrustedIps());
    }
  }, [dispatch, trustedIps, userData, getTrustedIpsStatus]);

  return (
    <div className={styles.container}>
      <LazyLoadImage src={logo} wrapperClassName={styles.logo} effect="blur" />
      {exchangesError ? (
        <ErrorComponent />
      ) : (
        <>
          <div className={styles.head}>
            {step === 0 && (
              <LazyLoadImage
                src={exchange}
                alt="exchange"
                effect="blur"
                height="200px"
                width="300px"
              />
            )}
            {step === 1 && (
              <LazyLoadImage
                src={risk1}
                alt="risk appetite"
                effect="blur"
                height="200px"
                width="300px"
              />
            )}

            {step < 6 && step > 1 && (
              <p style={{ fontSize: "16px", marginBottom: "1rem" }}>
                Let's see what features are suitable for you:
              </p>
            )}
          </div>
          <div className={styles.content}>
            {step === 0 && (
              <div className={styles.stepContainer}>
                <div className={styles.questionBox}>
                  <p style={{ textAlign: "center", marginBottom: "1rem" }}>
                    Integrate with an exchange, if you have any.
                  </p>
                  <TopExchanges showMore={setShowExchanges} />
                </div>
                <span className={styles.nextBtn} onClick={goToSecondStep}>
                  Next
                </span>
              </div>
            )}
            {step === 1 && (
              <div className={styles.stepContainer}>
                <div className={styles.questionBox}>
                  <p>
                    <BsQuestionCircle style={{ marginRight: ".5rem" }} />
                    What is your preferred risk appetite for signals?
                    <span className={styles.forceIcon}>*</span>
                  </p>
                  <div onClick={changeRiskHandler} className={styles.buttons}>
                    <span
                      title="Slow and Steady"
                      id="1"
                      className={`${styles.button} ${styles.riskBtn} ${
                        riskAppetite === "1" ? styles.active : ""
                      }`}
                    >
                      <FaShieldAlt className={styles.riskIcon} />
                      Very Cautious
                    </span>
                    <span
                      title="Safe and Sound"
                      id="2"
                      className={`${styles.button} ${styles.riskBtn} ${
                        riskAppetite === "2" ? styles.active : ""
                      }`}
                    >
                      <BsExclamationTriangleFill className={styles.riskIcon} />
                      Cautious
                    </span>
                    <span
                      title="Steady as She Goes"
                      id="3"
                      className={`${styles.button} ${styles.riskBtn} ${
                        riskAppetite === "3" ? styles.active : ""
                      }`}
                    >
                      <FaBalanceScale className={styles.riskIcon} />
                      Balanced
                    </span>
                    <span
                      title="High Wire Act"
                      id="4"
                      className={`${styles.button} ${styles.riskBtn} ${
                        riskAppetite === "4" ? styles.active : ""
                      }`}
                    >
                      <HiRocketLaunch className={styles.riskIcon} />
                      Adventurous
                    </span>
                    <span
                      title="All In"
                      id="5"
                      className={`${styles.button} ${styles.riskBtn} ${
                        riskAppetite === "5" ? styles.active : ""
                      }`}
                    >
                      <AiTwotoneFire className={styles.riskIcon} />
                      Very Adventurous
                    </span>
                  </div>
                </div>
                <div className={styles.handlers}>
                  <span className={styles.backBtn} onClick={() => setStep(0)}>
                    Back
                  </span>
                </div>
                <button
                  className={styles.submitButton}
                  onClick={(e) => handleClick(e)}
                >
                  Go to the Platform
                </button>
              </div>
            )}
          </div>
          <div className={styles.steps}>
            <span
              className={`${styles.step} ${step === 0 ? styles.active : ""}`}
            >
              1
            </span>
            <span
              className={`${styles.step} ${step === 1 ? styles.active : ""}`}
            >
              2
            </span>
          </div>
        </>
      )}
      <ExchangesModal show={showExchanges} setShow={setShowExchanges} />

      <ExchangeConnectionSettingsMenu />
    </div>
  );
};

export default StartWizard;
